import styled, { css } from 'styled-components';

/**
 * The signup page mirrors the Merchant Hub sign-in page (public/b2c/standard/css/walleyCommon.css): the same warm
 * gradient behind a dark card, Helvetica Neue / Arial, no radius on the card. Everything is self-contained so the
 * page looks the same whatever theme the signed-in app is in.
 */

export const focusRing = css`
    &:focus-visible {
        outline: 2px solid #8f91ea;
        outline-offset: 2px;
    }
`;

export const Page = styled.main`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
    box-sizing: border-box;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    color: #eeeeee;
    background-color: #f9f9f9;
    background-image: radial-gradient(ellipse 60% 70% at 0% 0%, #ff4d06 0%, transparent 100%),
        radial-gradient(ellipse 50% 60% at 60% 0%, #f0e792 0%, transparent 100%),
        radial-gradient(ellipse 30% 50% at 100% 0%, #ffdd00 0%, transparent 100%),
        radial-gradient(ellipse 90% 140% at 90% 100%, #e4007e 20%, transparent 100%),
        radial-gradient(ellipse 240% 190% at 20% 90%, #0d3843 10%, transparent 100%);
    background-repeat: no-repeat;
    background-size: 100% 100%;
    background-attachment: fixed;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }
`;

export const Card = styled.section`
    width: 100%;
    max-width: 470px;
    padding: 56px 44px;
    background: #1f1f1f;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);

    @media (max-width: 520px) {
        padding: 40px 20px;
    }
`;

export const Heading = styled.h1`
    margin: 0 0 28px;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.2;
    color: #eeeeee;
`;

export const SectionHeading = styled.h2`
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 700;
    color: #eeeeee;
`;

export const Field = styled.div`
    margin-bottom: 20px;
`;

/** Fields of one section, stacked. */
export const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

/** Postal code beside city. */
export const Row = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    gap: 12px;
`;

export const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    color: #b5b5b5;
`;

export const HelperText = styled.p`
    margin: 0 0 16px;
    font-size: 13px;
    color: #b5b5b5;
`;

export const Input = styled.input`
    width: 100%;
    min-width: 0;
    height: 52px;
    padding: 0 14px;
    background: #2b2b2b;
    border: 1px solid #5a5a5a;
    border-radius: 3px;
    color: #eeeeee;
    font-size: 16px;
    font-family: inherit;
    ${focusRing}

    &::placeholder {
        color: #7a7a7a;
    }

    &:disabled {
        background: #262626;
        color: #a8a8a8;
        cursor: not-allowed;
    }

    &[aria-invalid='true'] {
        border-color: #ff8a8a;
    }
`;

export const PrimaryButton = styled.button`
    width: 100%;
    height: 46px;
    background: #6a6cc8;
    color: #ffffff;
    font-size: 18px;
    font-family: inherit;
    border: 0;
    border-radius: 3px;
    cursor: pointer;
    ${focusRing}

    &:hover:not(:disabled) {
        background: #7b7dd6;
    }

    &:disabled {
        background: #3a3a4a;
        color: #a0a0b0;
        cursor: not-allowed;
    }
`;

export const ErrorText = styled.p`
    margin: 8px 0 0;
    font-size: 13px;
    color: #ff8a8a;
`;

export const Hint = styled.p`
    margin: 10px 0 0;
    font-size: 13px;
    color: #b5b5b5;
    text-align: center;
`;

export const Divider = styled.hr`
    margin: 28px 0;
    border: 0;
    border-top: 1px solid #383838;
`;

export const CountryGroup = styled.div`
    display: flex;
    gap: 8px;
`;

export const CountryButton = styled.button`
    flex: 1;
    min-height: 44px;
    background: #2b2b2b;
    border: 1px solid #5a5a5a;
    border-radius: 3px;
    color: #eeeeee;
    font-size: 15px;
    font-family: inherit;
    cursor: pointer;
    ${focusRing}

    &[aria-pressed='true'] {
        background: #6a6cc8;
        border-color: #6a6cc8;
        color: #ffffff;
    }
`;

export const Paragraph = styled.p`
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    color: #eeeeee;
`;

export const Footer = styled.footer`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    margin-top: 28px;
    font-size: 14px;
    color: #eeeeee;
    text-align: center;

    a {
        color: #eeeeee;
        ${focusRing}
    }
`;

export const Logo = styled.img`
    width: 250px;
    max-width: 100%;
    height: auto;
    margin-top: 8px;
`;
